import { cloneDeep } from 'lodash';

import type { Application, IPipeline, IServerGroup, IStage } from '@spinnaker/core';
import type {
  GceCommandHandlerName,
  GceConfigurationRefreshMethod,
  GceConfigurationUpdateMethod,
  GceServerGroupOperationMode,
  IGceServerGroupCommand,
  IGceServerGroupCommandBuilderAdapter,
  IGceServerGroupCommandDefaults,
  IGceServerGroupCommandUpdate,
  IGceServerGroupCommandUpdateResult,
  IGceServerGroupConfigurationAdapter,
  IGceServerGroupWizardAdapter,
} from './GceServerGroupWizard.types';

const EMPTY_UPDATE_RESULT: IGceServerGroupCommandUpdateResult = { dirty: {} };

export class GceServerGroupWizardAdapter implements IGceServerGroupWizardAdapter {
  public constructor(
    private commandBuilder: IGceServerGroupCommandBuilderAdapter,
    private configurationService: IGceServerGroupConfigurationAdapter,
  ) {}

  public buildNewServerGroupCommand(
    application: Application,
    defaults?: IGceServerGroupCommandDefaults,
  ): Promise<IGceServerGroupCommand> {
    return this.commandBuilder.buildNewServerGroupCommand(application, defaults);
  }

  public buildNewServerGroupCommandForPipeline(
    currentStage: IStage,
    pipeline: IPipeline,
  ): Promise<IGceServerGroupCommand> {
    return this.commandBuilder.buildNewServerGroupCommandForPipeline(currentStage, pipeline);
  }

  public buildServerGroupCommandFromExisting(
    application: Application,
    serverGroup: IServerGroup,
    mode?: GceServerGroupOperationMode,
  ): Promise<IGceServerGroupCommand> {
    return this.commandBuilder.buildServerGroupCommandFromExisting(application, serverGroup, mode);
  }

  public buildServerGroupCommandFromPipeline(
    application: Application,
    originalCluster: Partial<IGceServerGroupCommand>,
    currentStage: IStage,
    pipeline: IPipeline,
  ): Promise<IGceServerGroupCommand> {
    return this.commandBuilder.buildServerGroupCommandFromPipeline(
      application,
      originalCluster,
      currentStage,
      pipeline,
    );
  }

  public async configureCommand(
    application: Application,
    command: IGceServerGroupCommand,
  ): Promise<IGceServerGroupCommand> {
    const nextCommand = cloneDeep(command);
    await this.configurationService.configureCommand(application, nextCommand);
    return nextCommand;
  }

  public applyCommandHandler(
    command: IGceServerGroupCommand,
    handler: GceCommandHandlerName,
  ): Promise<IGceServerGroupCommandUpdate> {
    return this.applyUpdate(command, (nextCommand) => nextCommand[handler]?.(nextCommand));
  }

  public applyConfigurationUpdate(
    command: IGceServerGroupCommand,
    method: GceConfigurationUpdateMethod,
  ): Promise<IGceServerGroupCommandUpdate> {
    return this.applyUpdate(command, (nextCommand) => this.configurationService[method](nextCommand));
  }

  public applyConfigurationRefresh(
    command: IGceServerGroupCommand,
    method: GceConfigurationRefreshMethod,
    skipCommandReconfiguration?: boolean,
  ): Promise<IGceServerGroupCommandUpdate> {
    return this.applyUpdate(command, async (nextCommand) => {
      if (method === 'refreshInstanceTypes') {
        await this.configurationService.refreshInstanceTypes(nextCommand);
      } else {
        await this.configurationService[method](nextCommand, skipCommandReconfiguration);
      }
      return EMPTY_UPDATE_RESULT;
    });
  }

  private async applyUpdate(
    command: IGceServerGroupCommand,
    update: (
      nextCommand: IGceServerGroupCommand,
    ) => IGceServerGroupCommandUpdateResult | void | Promise<IGceServerGroupCommandUpdateResult | void>,
  ): Promise<IGceServerGroupCommandUpdate> {
    const nextCommand = cloneDeep(command);
    const result = (await update(nextCommand)) || EMPTY_UPDATE_RESULT;
    nextCommand.viewState = {
      ...nextCommand.viewState,
      dirty: { ...nextCommand.viewState.dirty, ...result.dirty },
    };
    nextCommand.processCommandUpdateResult?.(result);
    return { command: nextCommand, result };
  }
}
